import React, { useState, useEffect } from "react";
import { getDividends } from "../lib/api";
import { IndianRupee, TrendingUp, Calendar, Loader2, Info, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

export default function DividendsPage({ onStockClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getDividends();
        if (!cancelled) { setData(data); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError("Failed to load dividends"); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin text-[var(--brand)]" size={32} /></div>;
  if (error || !data) return <div className="p-8 text-center text-red-400">{error || "No data"}</div>;

  const s = data.summary;
  const payers = (data.holdings || []).filter(h => h.last_12m > 0);
  const topPayers = payers.slice(0, 8);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="dividends-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Dividends Dashboard</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Scanned {s.total_holdings_scanned} holdings · {s.dividend_payers} dividend-paying stocks
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard label="Received (last 12M)" value={fmtINR(s.total_last_12m)} icon={<IndianRupee size={16} />} testId="div-last12m" />
        <KPICard label={`This ${s.fy_label}`} value={fmtINR(s.total_this_fy)} icon={<Calendar size={16} />} testId="div-this-fy" />
        <KPICard label={`Last ${s.last_fy_label}`} value={fmtINR(s.total_last_fy)} icon={<Calendar size={16} />} sub="for comparison" testId="div-last-fy" />
        <KPICard label="Projected (next 12M)" value={fmtINR(s.total_projected_next_12m)} icon={<TrendingUp size={16} />} sub="assuming same policy" testId="div-projected" positive />
      </div>

      {/* Methodology banner */}
      <div className="bg-cyan-500/5 border border-cyan-500/15 rounded-md p-3 flex items-start gap-2" data-testid="methodology-banner">
        <Info size={14} className="text-cyan-400 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[var(--text-secondary)] leading-relaxed">
          <span className="text-cyan-400 font-semibold">How this is calculated:</span> {data.methodology}
        </p>
      </div>

      {/* Chart + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="monthly-chart">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">Monthly Dividend Flow (last 12M)</span>
            <span className="font-mono text-[10px] text-[var(--text-muted)]">{fmtINR(s.total_last_12m)} total</span>
          </div>
          {data.monthly.every(m => m.amount === 0) ? (
            <div className="flex items-center justify-center h-[180px] text-xs text-[var(--text-muted)]">No dividend receipts in the last 12 months.</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => v.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => v >= 1e5 ? `${(v/1e5).toFixed(1)}L` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v.toFixed(0)} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<MonthlyTooltip />} />
                <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                  {data.monthly.map((_, i) => <Cell key={i} fill="#10B981" fillOpacity={0.7} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="upcoming-dividends">
          <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">Expected Payments (next 12M)</div>
          {data.upcoming.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)]">No upcoming payments detected.</p>
          ) : (
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {data.upcoming.map((u, i) => (
                <div key={i} onClick={() => onStockClick?.(u.symbol)} className="flex items-center justify-between p-2 bg-[var(--bg-base)] rounded border border-white/5 hover:border-white/15 cursor-pointer transition-colors" data-testid={`upcoming-${u.symbol}`}>
                  <div>
                    <div className="font-mono text-xs text-white">{u.symbol}</div>
                    <div className="text-[9px] text-[var(--text-muted)]">≈ {u.expected_date}</div>
                  </div>
                  <div className="font-mono text-xs text-emerald-400">{fmtINR(u.estimated_amount)}</div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[9px] text-[var(--text-muted)] mt-2 italic">Estimated from prior year dates.</p>
        </div>
      </div>

      {/* Top contributors */}
      {topPayers.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="top-payers-chart">
          <div className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold mb-3">Top Dividend Contributors (last 12M)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={topPayers} margin={{ top: 5, right: 10, left: 0, bottom: 0 }} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={v => v >= 1e5 ? `${(v/1e5).toFixed(1)}L` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : v.toFixed(0)} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="symbol" tick={{ fill: "#D1D5DB", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={100} />
              <Tooltip content={<ContributorTooltip />} />
              <Bar dataKey="last_12m" radius={[0, 3, 3, 0]} fill="#10B981" fillOpacity={0.75} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Holdings table */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="dividends-table">
        <div className="p-3 border-b border-white/10 flex items-center justify-between">
          <span className="text-xs tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">Per-Holding Details</span>
          <span className="text-[10px] text-[var(--text-muted)]">Non-payers hidden below</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold pl-4">Stock</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Account</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Qty</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Last DPS</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Last 12M</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">This FY</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Projected</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right">Yield on Cost</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold text-right pr-4">Payments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payers.map((h, i) => (
                <TableRow key={i} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => onStockClick?.(h.symbol)} data-testid={`div-row-${h.symbol}`}>
                  <TableCell className="pl-4"><span className="font-mono text-sm text-white">{h.symbol}</span></TableCell>
                  <TableCell><span className="text-xs text-[var(--text-muted)]">{h.account_name}</span></TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{h.quantity}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{h.currency === "USD" ? "$" : "₹"}{h.last_dps.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-emerald-400">{fmtINR(h.last_12m)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-white">{fmtINR(h.this_fy)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-cyan-400">{fmtINR(h.projected_next_12m)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-[var(--text-secondary)]">{h.yield_on_cost_pct}%</TableCell>
                  <TableCell className="text-right pr-4 font-mono text-xs text-[var(--text-muted)]">{h.payments_last_12m}</TableCell>
                </TableRow>
              ))}
              {payers.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-xs text-[var(--text-muted)]">No dividend-paying stocks found in your portfolio.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, sub, icon, positive, testId }) {
  const tone = positive ? "investment" : "income";
  return (
    <div data-tone={tone} className="semantic-card border rounded-md p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] tracking-[0.1em] uppercase text-[var(--text-muted)] font-semibold">{label}</span>
        <span className={positive ? "text-[var(--positive)]" : "text-[var(--brand)]"}>{icon}</span>
      </div>
      <div className="semantic-value font-mono text-xl font-medium tracking-tight">{value}</div>
      {sub && <span className="font-mono text-[10px] text-[var(--text-muted)]">{sub}</span>}
    </div>
  );
}

function MonthlyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl"><div className="text-[var(--text-muted)] font-mono mb-0.5">{label}</div><div className="font-mono text-emerald-400 font-semibold">{fmtINR(v)}</div></div>;
}

function ContributorTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return <div className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-xs shadow-xl"><div className="text-white font-mono">{d.symbol}</div><div className="font-mono text-emerald-400">{fmtINR(d.last_12m)} last 12M</div><div className="text-[10px] text-[var(--text-muted)]">{d.payments_last_12m} payments · {d.yield_on_cost_pct}% YoC</div></div>;
}

function fmtINR(v) { if (!v && v !== 0) return "--"; const abs = Math.abs(v); if (abs >= 1e7) return `₹${(v/1e7).toFixed(2)}Cr`; if (abs >= 1e5) return `₹${(v/1e5).toFixed(2)}L`; if (abs >= 1e3) return `₹${(v/1e3).toFixed(1)}K`; return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`; }
